import React, { useContext, useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import EmployeeBaseLocationsSelect from '../../../components/EmployeeBaseLocationsSelect'
import { ActionPanelItem } from './EmployeeApplicationActionPanel'
import SmallModal from '../../../components/Modals/SmallModal'
import BigModal from '../../../components/Modals/BigModal'
import API from '../../../services/API'
import { useNavigate } from 'react-router-dom'
import { GlobalContext } from '../../../App'
import UpgradeRequired from '../../../components/Upgrade/UpgradeGate'
import SubscriptionGate from '../../../components/Subscriptions/SubscriptionGate'
import planAllowances from '../../../helpers/planAllowances.json'

// Figures come from the plan catalogue mirror, never typed in here.
const premiumEmployees = planAllowances.plans.premium.allowances.employees
const enterpriseEmployees = planAllowances.plans.enterprise.allowances.employees

const AcceptApplication = ({ applicationID, disabled }) => {
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [limitInfo, setLimitInfo] = useState({})
  const [limitReached, setLimitReached] = useState(false)
  const [baseLocations, setBaseLocations] = useState([])
  const [acceptError, setAcceptError] = useState(null)

  const { tenantInfo, baseLocationOptions } = useContext(GlobalContext)
  const { subscriptionTier } = tenantInfo ?? {}

  useEffect(() => {
    API.get('/subscription/usage').then((res) => {
      if (res.status === 200) {
        const { used, limit } = res.data.employees
        setLimitInfo({ used, limit })
        if (Number(used) >= Number(limit)) {
          setLimitReached(true)
        }
      }
    })
  }, [])
  const navigate = useNavigate()
  const handleAcception = () => {
    API.post('/accept-employees-application', {
      applicationID,
      permittedBaseLocations: baseLocations,
    })
      .then((res) => {
        if (res.status === 200) {
          navigate('/employees/' + res.data.newEmployeeID)
        } else if (res.status === 403) {
          setLimitReached(true)
        } else if (res.data?.errorCode === 'baseLocationRequired') {
          setAcceptError(res.data.error)
        }
      })
      .catch((e) => {
        if (e.response === 403) {
          setLimitReached(true)
        }
      })
  }

  const handleOpenConfirmation = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setOpenConfirmation(true)
  }

return (
  <ActionPanelItem>
    <Button
      variant="outlined"
      color="success"
      fullWidth
      size="small"
      onClick={handleOpenConfirmation}
      disabled={disabled}
    >
      Accept Application
    </Button>

    {openConfirmation &&
      (limitReached ? (
        <BigModal
          autoWidth
          open
          onClose={() => setOpenConfirmation(false)}
        >
          <SubscriptionGate
            requiredTier={subscriptionTier === 2 ? 3 : 12}
            featureName="Employee Limit Reached"
            caption={`Your current subscription allows ${limitInfo.limit} active employees and your organisation already has ${limitInfo.used} active employees.`}
            benefits={
              subscriptionTier === 1
                ? [
                    `Premium Subscription: ${premiumEmployees} Active Employees`,
                    `Enterprise Subscription: ${enterpriseEmployees} Active Employees`,
                  ]
                : subscriptionTier === 2
                  ? [
                      `Enterprise Subscription: ${enterpriseEmployees} Active Employees`,
                    ]
                  : ['Purchase additional employee capacity']
            }
            inline
          />
        </BigModal>
      ) : (
        <BigModal
          autoWidth
          open
          onClose={() => setOpenConfirmation(false)}
        >
          <Stack gap={1} alignItems="center">
            <Typography variant="title3">
              Are you sure you want to accept this application?
            </Typography>

            <Typography variant="caption">
              Doing so will create an employee account, allow the user to log in
              as an employee, and send them a notification email.
            </Typography>

            {baseLocationOptions?.length > 0 ? (
              <EmployeeBaseLocationsSelect
                value={baseLocations}
                onChange={(ids) => {
                  setAcceptError(null)
                  setBaseLocations(ids)
                }}
                helperText="Assign the new starter to their base location(s)"
              />
            ) : (
              <Alert severity="warning">
                Add a base location before accepting applications.
              </Alert>
            )}

            {acceptError && <Alert severity="error">{acceptError}</Alert>}

            <ButtonGroup>
              <Button
                variant="contained"
                color="error"
                onClick={() => setOpenConfirmation(false)}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={handleAcception}
                disabled={!baseLocations.length}
              >
                Accept
              </Button>
            </ButtonGroup>
          </Stack>
        </BigModal>
      ))}
  </ActionPanelItem>
)
}

export default AcceptApplication
